<script setup>
import { computed, ref } from 'vue';

import { frontendURL } from 'dashboard/helper/URLHelper.js';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useInbox } from 'dashboard/composables/useInbox';
import InboxName from 'dashboard/components/widgets/InboxName.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import ContactAPI from 'dashboard/api/contacts';
import {
  useMapGetter,
} from 'dashboard/composables/store.js';
import { useStore } from 'dashboard/composables/store';
import { useStoreGetters } from 'dashboard/composables/store';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const router = useRouter();
const store = useStore();

const props = defineProps({
  id: {
    type: Number,
    default: 0,
  },
  inbox: {
    type: Object,
    default: () => ({}),
  },
  name: {
    type: String,
    default: '',
  },
  email: {
    type: String,
    default: '',
  },
  accountId: {
    type: [String, Number],
    default: '',
  },
  createdAt: {
    type: [String, Date, Number],
    default: '',
  },
  messageId: {
    type: Number,
    default: 0,
  },
  emailSubject: {
    type: String,
    default: '',
  },
});
const conversationId = ref(null);
const getters = useStoreGetters();
const currentUser = useMapGetter('getCurrentUser');

const { inbox } = useInbox(props.inbox?.id);

const navigateTo = computed(() => {
  const params = {};
  if (props.messageId) {
    params.messageId = props.messageId;
  }
  return frontendURL(
    `accounts/${props.accountId}/conversations/${props.id}`,
    params
  );
});
const conversation = computed(
  () => getters.getConversationById.value(props.id) || {}
);

const createdAtTime = computed(() => {
  if (!props.createdAt) return '';
  return dynamicTime(props.createdAt);
});

const infoItems = computed(() => [
  {
    label: 'SEARCH.FROM',
    value: props.name,
    show: !!props.name,
  },
  {
    label: 'SEARCH.EMAIL',
    value: props.email,
    show: !!props.email,
  },
  {
    label: 'SEARCH.EMAIL_SUBJECT',
    value: props.emailSubject,
    show: !!props.emailSubject,
  },
]);
const { t } = useI18n();
const { isAdmin } = useAdmin();


const visibleInfoItems = computed(() =>
  infoItems.value.filter(item => item.show)
);
function showError(message) {
  useAlert(message );
}
const navigateToConversation = () => {

  const params = {};
  if (props.messageId) {
        params.messageId = props.messageId;
      }
     const url = frontendURL(
        `accounts/${props.accountId}/conversations/${props.id}`,
        params
      );
      router.push(url);

}
const openConversation = async () => {
  //reload conversation
  try {

    const conversationData = await store.dispatch('fetchConversationById', { conversationId: props.id , accountId: props.accountId});

    //group
    if (conversationData.meta?.sender?.identifier?.endsWith('@g.us')){ // todo @ change to
      return navigateToConversation();
    }

    if (isAdmin.value) {
      return navigateToConversation();
    }

    const assignee = conversationData.meta?.assignee

    //if the conversation is already assigned to the current user,
    if (assignee && assignee.id === currentUser.value.id) {
      return navigateToConversation();
    }

    //if the conversation is assigned to another agent,
    if (assignee && assignee.id !== currentUser.value.id) {
      showError(t('CONVERSATION.SEARCH.ALREADY_ASSIGNED_MESSAGE'));
      return;
    }

    //agent get the oldest unassigned open conversation
    const oldestId = await store.dispatch('fetchOldestUnassignedConversation');
    if (conversationData.status === 'open' && (!oldestId || conversationData.id !== oldestId)){
      useAlert(t('CONVERSATION.ERROR_ONLY_OLDEST'));
      return;
    }

    //if the conversation is unassigned, self-assign it to the current user
    if (!assignee && conversationData.status === 'open') {
      //assign to agent only if the conversation is open
      onClickSelfAssign(conversationData);
      return navigateToConversation();
    }

    //if the conversation is not open
    if (conversationData.status !== 'open') {
      return navigateToConversation();
    }

    //TODO: Validte what should happend with peding status

  } catch (err) {
     showError(t('CONVERSATION.SEARCH.CONVERSATION_OPEN_ERROR'));
     console.error('Error al abrir conversación', err);
  }
}

const onClickSelfAssign = (chat) => {

  const {
    account_id,
    availability_status,
    available_name,
    email,
    id,
    name,
    role,
    avatar_url,
  } = currentUser.value;

  const selfAssign = {
    account_id,
    availability_status,
    available_name,
    email,
    id,
    name,
    role,
    thumbnail: avatar_url,
  };

  //this.$set(this.isLoadingAssign, chat.id, true);

  // 1. Actualiza localmente el chat si tienes lógica para eso
  if(chat.meta) {
    chat.meta.assignee = selfAssign;
  }

  // 2. Envía a Vuex
  //this.$store.dispatch('setCurrentChatAssignee', this.currentUser);
  store
    .dispatch('assignAgent', {
      conversationId: chat.id,
      agentId: id,
    })
    .then(() => {
      this.$toast && this.$toast.success(this.$t('CONVERSATION.CHANGE_AGENT'));
    })
    .finally(() => {
      // this.$set(this.isLoadingAssign, chat.id, false);
    });
}

const inboxName = computed(() => props.inbox?.name);

const inboxIcon = computed(() => {
  if (!inbox.value) return null;
  const { channelType, medium } = inbox.value;
  return getInboxIconByType(channelType, medium);
});
</script>

<template>
  <Button
    @click="openConversation"
    class="flex p-2 rounded-xl cursor-pointer hover:bg-n-slate-2 w-100 w-full"
    >
    <CardLayout
      layout="col"
      class="[&>div]:justify-start [&>div]:gap-2 [&>div]:px-4 [&>div]:py-3 [&>div]:items-start hover:bg-n-slate-2 dark:hover:bg-n-solid-3"
    >
      <div
        class="flex items-center min-w-0 justify-between gap-2 w-full h-7 mb-1"
      >
        <div class="flex items-center gap-3">
          <div class="flex items-center gap-1.5 flex-shrink-0">
            <Icon
              icon="i-lucide-hash"
              class="flex-shrink-0 text-n-slate-11 size-4"
            />
            <span class="text-n-slate-12 text-sm leading-4">
              {{ id }}
            </span>
          </div>
          <div v-if="inboxName" class="w-px h-3 bg-n-strong" />
          <div v-if="inboxName" class="flex items-center gap-1.5 flex-shrink-0">
            <div
              v-if="inboxIcon"
              class="flex items-center justify-center flex-shrink-0 rounded-full bg-n-alpha-2 size-4"
            >
              <Icon
                :icon="inboxIcon"
                class="flex-shrink-0 text-n-slate-11 size-2.5"
              />
            </div>
            <span class="text-sm leading-4 text-n-slate-12">
              {{ inboxName }}
            </span>
          </div>
        </div>
        <span
          v-if="createdAtTime"
          class="text-sm font-normal min-w-0 truncate text-n-slate-11"
        >
          {{ createdAtTime }}
        </span>
      </div>
      <div class="flex flex-wrap gap-x-2 gap-y-1.5 items-center">
        <template
          v-for="(item, index) in visibleInfoItems"
          :key="`info-${index}`"
        >
          <h5 class="m-0 text-sm min-w-0 text-n-slate-12 truncate">
            <span class="text-sm leading-4 font-normal text-n-slate-11">
              {{ $t(item.label) + ':' }}
            </span>
            {{ item.value }}
          </h5>
          <div
            v-if="index < visibleInfoItems.length - 1"
            class="w-px h-3 bg-n-strong"
          />
        </template>
      </div>
      <slot />
    </CardLayout>
  </Button>
</template>
